import React, { useEffect } from "react";
import { Row, Col, Button, Form, Switch, Checkbox, Modal as AntModal } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { Controller, useForm } from "react-hook-form";
import axios from "axios";
import Constants from "../../Utils/Constants";
import useToken from "../../Authentication/useToken";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import ProfessionalDetailsIdentityFields from "../shared/ProfessionalDetailsIdentityFields";

const kinesiologistDetailsFieldKeys = {
  labelFirstName: "Kinesiologist.label_first_name",
  errorRequiredFirstName: "Kinesiologist.error_required_first_name",
  errorFirstNameMinLength: "Kinesiologist.error_first_name_min_length",
  labelLastName: "Kinesiologist.label_last_name",
  errorRequiredLastName: "Kinesiologist.error_required_last_name",
  errorLastNameMinLength: "Kinesiologist.error_last_name_min_length",
  labelEmail: "Kinesiologist.label_email",
  errorRequiredEmail: "Kinesiologist.error_required_email",
  errorInvalidEmailFormat: "Kinesiologist.error_invalid_email_format",
  labelPhoneNumber: "Kinesiologist.label_phone_number",
  errorRequiredPhoneNumber: "Kinesiologist.error_required_phone_number",
  errorInvalidPhoneNumber: "Kinesiologist.error_invalid_phone_number",
};

function KinesiologistDetails({
  kinesiologist,
  onClose,
  refetchKinesiologists
}) {
  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm();
  const { token } = useToken();
  const { t } = useTranslation("Professionals");

  // Pré-remplir le formulaire avec les données du kinésiologue
  useEffect(() => {
    if (kinesiologist) {
      setValue("firstname", kinesiologist.firstname);
      setValue("lastname", kinesiologist.lastname);
      setValue("email", kinesiologist.email);
      setValue("phoneNumber", kinesiologist.phoneNumber);
      setValue("active", kinesiologist.active);
      const roles = Array.isArray(kinesiologist.roles)
        ? kinesiologist.roles.map((r) => String(r).toLowerCase())
        : [String(kinesiologist.role || "").toLowerCase()];
      setValue("assignManagerRole", roles.includes("clinic_manager"));
    }
  }, [kinesiologist, setValue]);

  const onSubmit = (data) => {
    const kinesiologistData = {
      ...data,
      role: "kinesiologist",
      assignManagerRole: Boolean(data.assignManagerRole),
    };

    axios
      .put(
        `${Constants.SERVER_URL}/update-professional-user/${kinesiologist.id}`,
        kinesiologistData,
        {
          headers: { Authorization: "Bearer " + token },
        }
      )
      .then(() => {
        refetchKinesiologists();
        AntModal.success({
          content: t("Kinesiologist.success_updating_msg"),
          okText: t("Kinesiologist.button_close"),
          centered: true,
          onOk: () => {
            onClose();
          },
        });
      })
      .catch((err) => {
        const errorMessage =
          t(`Backend:${err.response?.data?.message}`) ||
          t("Kinesiologist.error_updating_kinesiologist");
        AntModal.error({
          content: errorMessage,
          okText: t("Kinesiologist.button_close"),
          centered: true,
        });
      });
  };

  return (
    <Row justify="center">
      <Col span={16}>
        <Form layout="vertical" onFinish={handleSubmit(onSubmit)}>
          <ProfessionalDetailsIdentityFields
            t={t}
            control={control}
            errors={errors}
            keys={kinesiologistDetailsFieldKeys}
          />

          <Row gutter={16}>
            {/* Status field */}
            <Col span={12}>
              <Form.Item label={t("Kinesiologist.label_status")}>
                <Controller
                  name="active"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <Switch
                      checked={value}
                      onChange={onChange}
                      checkedChildren={t("Kinesiologist.active_status")}
                      unCheckedChildren={t("Kinesiologist.inactive_status")}
                    />
                  )}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Controller
              name="assignManagerRole"
              control={control}
              defaultValue={false}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  checked={Boolean(value)}
                  onChange={(e) => onChange(e.target.checked)}
                >
                  Attribuer aussi le role de gestionnaire
                </Checkbox>
              )}
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
              {t("Kinesiologist.button_update_kinesiologist")}
            </Button>
          </Form.Item>
        </Form>
      </Col>
    </Row>
  );
}

KinesiologistDetails.propTypes = {
  kinesiologist: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  refetchKinesiologists: PropTypes.func.isRequired,
};

export default KinesiologistDetails;
